import React, { useEffect, useState } from "react";
import ContractOverview from "../../../../components/cards/ContractOverview";
import OrderDetailTable from ".././OrderDetailTable";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { fetchOrderById } from "../../../../redux/features/orderSlice";
import moment from "moment";
import UpdateDispatchStatus from "../UpdateDispatchStatus";
import { formatNumberWithCommas } from "../../../../helpers";
import { Check } from "lucide-react";

const ReturnedOrderDetails: React.FC = () => {
  const dispatch = useAppDispatch();
  const { selectedOrder } = useAppSelector((state) => state.orders);
  const { id } = useParams();
  const [openUpdateDispatchModal, setOpenUpdateDispatchModal] = useState(false);

  useEffect(() => {
    dispatch(fetchOrderById(id));
  }, [id, dispatch]);

  return (
    <div className="p-6">
      {/* Back Button */}
      <button
        className="text-gray-500 hover:text-gray-800 flex items-center font-bold mb-4 bg-gray-50 p-2 rounded"
        onClick={() => window.history.back()}
      >
        &larr; Back
      </button>

      <ContractOverview
        contractId={`INT-${selectedOrder?.contract?.contractCode}`}
        shopName={selectedOrder?.contract?.title ?? ""}
        status={selectedOrder?.status ?? ""}
        chatUp={false}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6 mb-6">
        {/* Combined Overview and Order Details Section */}
        <div className="col-span-2 lg:col-span-2 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          {/* Overview Section */}
          <h3 className="font-semibold text-lg mb-4">Overview</h3>
          <div className="space-y-3">
            {[
              {
                label: "Created at",
                value: new Date(
                  selectedOrder?.createdAt ?? ""
                ).toLocaleDateString(),
              },
              {
                label: "Pickup Courier",
                value: selectedOrder?.pickupCourier?.name ?? "N/A",
              },
              {
                label: "Drop off Courier",
                value: selectedOrder?.dropoffCourier?.name ?? "N/A",
              },
              {
                label: "Payment method",
                value: selectedOrder?.paymentOption ?? "N/A",
              },
              {
                label: "Name of vendor",
                value: selectedOrder?.vendor?.fullName ?? "N/A",
              },
              {
                label: "Name of buyer",
                value: selectedOrder?.buyer?.fullName ?? "N/A",
              },
              { label: "Delivery option", value: "Pick-up Station" },
            ].map((detail, index) => (
              <div key={index} className="flex justify-between text-sm">
                <p className="text-gray-600">{detail.label}</p>
                <p className="font-medium text-gray-800">{detail.value}</p>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t my-6"></div>

          {/* Addresses */}
          <div className=" md:flex items-baseline gap-10  w-full mt-6 space-y-4">
            <div>
              <h4 className="font-semibold">Shipping Address (Vendor)</h4>
              <p className="text-gray-600">
                {selectedOrder?.dropoffLocation?.address || "N/A"}
              </p>
            </div>
            <div>
              <h4 className="font-semibold">Shipping Address (Buyer)</h4>
              <p className="text-gray-600">
                {selectedOrder?.pickupLocation?.address || "N/A"}
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t my-6"></div>

          {/* Order Details Section */}
          <h3 className="font-semibold text-lg mb-4">Order Details</h3>
          <div className="space-y-3">
            {[
              {
                label: "Order Amount",
                value: `₦${formatNumberWithCommas(
                  Number(selectedOrder?.orderAmount)
                )}`,
              },
              {
                label: "Status",
                value: selectedOrder?.status,
                color:
                  selectedOrder?.status === "PAID" ||
                  selectedOrder?.status === "COMPLETED"
                    ? "text-green-500 bg-green-200 p-1 border border-green-500 rounded"
                    : selectedOrder?.status === "PENDING"
                    ? "text-yellow-500 bg-yellow-200 p-1 border border-yellow-500 rounded"
                    : "text-red-500 bg-red-200 p-1 border border-red-500 rounded",
              },
              {
                label: "Dispatch Status",
                value: selectedOrder?.dispatchStatus,
                color:
                  selectedOrder?.dispatchStatus === "Pending"
                    ? "text-yellow-500 bg-yellow-200 p-1 border border-yellow-500 rounded text-xs"
                    : selectedOrder?.dispatchStatus === "Dispatch"
                    ? "text-blue-500 bg-blue-200 p-1 border border-blue-500 rounded text-xs"
                    : selectedOrder?.dispatchStatus === "InTransit"
                    ? "text-purple-500 bg-purple-200 p-1 border border-purple-500 rounded text-xs"
                    : selectedOrder?.dispatchStatus === "Arrived"
                    ? "text-orange-500 bg-orange-200 p-1 border border-orange-500 rounded text-xs"
                    : selectedOrder?.dispatchStatus === "Delivered"
                    ? "text-green-500 bg-green-200 p-1 border border-green-500 rounded text-xs"
                    : "text-gray-500 bg-gray-200 p-1 border border-gray-500 rounded text-xs", // Default style for unknown statuses
              },
              {
                label: "Courier Fee Status",
                value: selectedOrder?.courierFeePaymentStatus,
                color:
                  selectedOrder?.courierFeePaymentStatus === "PENDING"
                    ? "text-yellow-500 bg-yellow-200 p-1 border border-yellow-500 rounded text-xs"
                    : "text-green-500 bg-green-200 p-1 border border-green-500 rounded text-xs",
              },
              Number(selectedOrder?.actualCourierFee) -
                Number(selectedOrder?.estimatedCourierFee) >
                0 && {
                label: "Courier Fee Balance",
                value: (
                  <div className="flex items-center gap-2">
                    <span>
                      ₦
                      {Number(selectedOrder?.actualCourierFee) -
                        Number(selectedOrder?.estimatedCourierFee)}
                    </span>
                  </div>
                ),
                color: "text-red-500 text-md", // Default style for unknown statuses
              },
              {
                label: "Estimated Delivery Date",
                value: new Date(
                  selectedOrder?.contract?.estimatedDeliveryDate
                ).toLocaleDateString(),
              },
            ]?.map((detail: any, index) => (
              <div key={detail?.label} className="flex justify-between text-sm">
                <p className="text-gray-600">{detail.label}</p>
                <p
                  className={`text-xs font-medium ${
                    detail.color ?? "text-gray-800"
                  }`}
                >
                  {detail.value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Track Order Section */}
        <div className="col-span-2 lg:col-span-1 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center  justify-between w-full">
            <h3 className="font-semibold text-lg mb-4">Track Order</h3>
            {selectedOrder?.status === "PAID" && (
              <button
                className="bg-none border-none text-accent mb-4 font-bold"
                onClick={() => setOpenUpdateDispatchModal(true)}
              >
                Update Status
              </button>
            )}
          </div>
          <div className="space-y-3">
            {[
              { status: "Order Placed", completed: true },
              { status: "Dispatch", completed: false },
              { status: "InTransit", completed: false },
              { status: "Arrived", completed: false },
              { status: "Delivered", completed: false },
            ].map((item: any) => {
              // Check if the current status exists in the trackings
              const tracking = selectedOrder?.trackings?.find(
                (tracking: any) => tracking.dispatchStatus === item.status
              );

              // Determine if the indicator should be green
              const isGreen = tracking?.dispatchStatus ?? false; // Green if tracking exists

              return (
                <div key={item?.id} className="flex items-center space-x-3">
                  {/* Status Indicator */}
                  <div
                    className={`flex items-center justify-center w-4 h-4 rounded-full ${
                      isGreen || item?.completed
                        ? "bg-green-500"
                        : "bg-gray-300"
                    }`}
                  >
                    {(isGreen || item?.completed) && <Check color="#fff" />}
                  </div>
                  <div className="text-xs">
                    <p
                      className={`font-medium ${
                        isGreen || item?.completed
                          ? "text-gray-800"
                          : "text-gray-500"
                      }`}
                    >
                      {item.status}
                    </p>
                    <p className="text-gray-500 text-xs">
                      {tracking?.timestamp
                        ? moment(tracking.timestamp).format("Do MMM YYYY")
                        : "N/A"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <OrderDetailTable />

      <UpdateDispatchStatus
        isOpen={openUpdateDispatchModal}
        onClose={() => setOpenUpdateDispatchModal(false)}
        order={selectedOrder}
        onSave={() => dispatch(fetchOrderById(id))}
      />
    </div>
  );
};

export default ReturnedOrderDetails;
